import React, { useEffect, useRef, useState } from "react";

type Dataset = {
  label: string;
  color?: string;
  points: { x: number; y: number }[];
};

type LineChartProps = {
  datasets: Dataset[];
  width?: number;
  height?: number;
  pointSpacing?: number;
  xLabel?: string;
  yLabel?: string;
  loading?: boolean;
  onLoadMore?: () => void;
  customTooltip?: (value: { dataset: string; x: any; y: any }) => React.ReactNode;
  customXValue?: (value:number)=>React.ReactNode;
  onClickPoint?: (value: { dataset: string; x: any; y: any })=>void;
};

const LineChart: React.FC<LineChartProps> = ({
  datasets,
  width = 600,
  height = 300,
  pointSpacing = 80,
  xLabel = "X Axis",
  yLabel = "Y Axis",
  loading,
  onLoadMore = () => null,
  customTooltip = ({ dataset, x, y }) => (
    <>
      <div>
        <strong>{dataset}</strong>
      </div>
      <div>
        <strong>X:</strong> {x}
      </div>
      <div>
        <strong>Y:</strong> {y}
      </div>
    </>
  ),
  customXValue: xValue=null,
  onClickPoint=()=>null
}) => {
  const [yMax, setYMax] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ✅ Flatten all X values across datasets
  const allXValues = Array.from(
    new Set(datasets.flatMap((ds) => ds.points.map((d) => d.x)))
  ).sort((a, b) => (a > b ? 1 : -1));

  // ✅ Flatten all Y values to find max
  const allYValues = datasets.flatMap((ds) => ds.points.map((d) => d.y));

  // Update global max Y
  useEffect(() => {
    if (allYValues.length > 0) {
      const maxVal = Math.max(...allYValues);
      setYMax((prev) => Math.max(prev, maxVal));
    }
  }, [allYValues, datasets]);

  // ✅ Detect scroll end to load more
  useEffect(() => {
    const div = scrollRef.current;
    if (!div) return;

    const handleScroll = () => {
      if (loading) return;
      if (div.scrollLeft + div.clientWidth >= div.scrollWidth - 10) {
        onLoadMore();
      }
    };

    div.addEventListener("scroll", handleScroll);
    return () => div.removeEventListener("scroll", handleScroll);
  }, [loading, onLoadMore]);

  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    value: { dataset: string; x: any; y: number };
  } | null>(null);

  // if (datasets.length === 0 || allXValues.length === 0)
  //   return null;

  const padding = { top: 20, right: 20, bottom: 40 };
  const leftGutter = 50;

  // Full width grows with number of unique X values
  const fullWidth = Math.max(width, allXValues.length * pointSpacing + 40);

  const yMin = 0;
  const effectiveYMax = yMax ?? Math.max(...allYValues, 1);

  // ✅ Scale functions
  const scaleX = (xVal: any) => {
    const index = allXValues.indexOf(xVal);
    return (
      (index / (allXValues.length - 1)) * (fullWidth - leftGutter - 20) +
      leftGutter
    );
  };

  const scaleY = (y: number) =>
    height -
    padding.bottom -
    ((y - yMin) / (effectiveYMax - yMin)) *
      (height - padding.top - padding.bottom);

  // ✅ Y ticks
  const yTicks = 5;
  const yStep = (effectiveYMax - yMin) / yTicks;
  const yTickValues = Array.from(
    { length: yTicks + 1 },
    (_, i) => yMin + i * yStep
  );

  return (
    <div style={{position: "relative"}}>
      <div>
        <div style={{ display: "flex", position: "relative" }}>
          {/* Y Axis (fixed) */}
          <svg width={50} height={height}>
            {yTickValues.map((val, i) => {
              const y = scaleY(val);
              return (
                <text key={i} x={45} y={y + 4} textAnchor="end" fontSize="10">
                  {effectiveYMax < 10 ? val.toFixed(2) : Math.round(val)}
                </text>
              );
            })}
            <line
              x1={45}
              y1={padding.top}
              x2={45}
              y2={height - padding.bottom}
              stroke="black"
            />
            <text
              x={15}
              y={height / 2}
              textAnchor="middle"
              fontSize="12"
              transform={`rotate(-90, 15, ${height / 2})`}
            >
              {yLabel}
            </text>
          </svg>

          {/* Scrollable Chart */}
          <div ref={scrollRef} style={{ width, overflowX: "auto" }}>
            <svg width={fullWidth} height={height}>
              {/* Y gridlines */}
              {yTickValues.map((val, i) => {
                const y = scaleY(val);
                return (
                  <line
                    key={i}
                    x1={0}
                    x2={fullWidth}
                    y1={y}
                    y2={y}
                    stroke="#eee"
                  />
                );
              })}

              {/* X ticks */}
              {allXValues.map((xVal, i) => {
                const x = scaleX(xVal);
                return (
                  <g key={i}>
                    <line
                      x1={x}
                      x2={x}
                      y1={padding.top}
                      y2={height - padding.bottom}
                      stroke="#f5f5f5"
                    />
                    <text
                      x={x}
                      y={height - padding.bottom + 25}
                      textAnchor="middle"
                      fontSize="10"
                      transform={`rotate(0, ${x}, ${height -
                        padding.bottom +
                        25})`}
                    >
                      {!!xValue?xValue(xVal):xVal}
                    </text>
                  </g>
                );
              })}

              {/* X axis */}
              <line
                x1={0}
                y1={height - padding.bottom}
                x2={fullWidth}
                y2={height - padding.bottom}
                stroke="black"
              />

              {/* Lines + Points */}
              {datasets.map((ds, idx) => {
                const pathData = ds.points
                  .map(
                    (d, i) =>
                      `${i === 0 ? "M" : "L"} ${scaleX(d.x)},${scaleY(d.y)}`
                  )
                  .join(" ");
                return (
                  <g key={idx}>
                    <path
                      d={pathData}
                      fill="none"
                      stroke={ds.color}
                      strokeWidth="2"
                    />
                    {ds.points.map((d, i) => (
                      <circle
                        key={i}
                        cx={scaleX(d.x)}
                        cy={scaleY(d.y)}
                        r={4}
                        fill="white"
                        stroke={ds.color}
                        strokeWidth="2"
                        onMouseEnter={() =>
                          setTooltip({
                            x: scaleX(d.x),
                            y: scaleY(d.y),
                            value: { dataset: ds.label, ...d },
                          })
                        }
                        onMouseLeave={() => setTooltip(null)}
                        style={{cursor:"pointer"}}
                        onClick={()=>{
                            onClickPoint({ dataset: ds.label, ...d });
                        }}
                      />
                    ))}
                  </g>
                );
              })}
            </svg>
            {/* Fixed X axis label */}
            <div style={{ display: "flex" }}>
              <svg width={50} height={40} /> {/* spacer for Y axis */}
              <svg width={width} height={40}>
                <text x={width / 2} y={30} textAnchor="middle" fontSize="12">
                  {xLabel}
                </text>
              </svg>
            </div>
          </div>

        </div>

        {/* Tooltip */}
        {tooltip && (
          <div
            style={{
              position: "absolute",
              top: tooltip.y - 40,
              left: tooltip.x + 50 - (scrollRef?.current?.scrollLeft ?? 0),
              background: "white",
              border: "1px solid #ccc",
              padding: "6px 10px",
              borderRadius: "6px",
              boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
              pointerEvents: "none",
              fontSize: "12px",
              whiteSpace: "nowrap",
            }}
          >
            {customTooltip(tooltip.value)}
          </div>
        )}
      </div>

      {loading && <div
      style={{ position: "absolute", top:"25%", left:0, width:"100%", height:"25%", backgroundColor: "rgba(0, 0, 0, 0.7)", display:"flex", alignItems:"center"}}
      ><div style={{width:"content-fit", margin:"0 auto 0 auto"}}>Loading...</div></div>}
    </div>
  );
};

export default LineChart;
